import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useParams } from 'react-router-dom';
import { PayerIdentityChoice } from '../components/PayerIdentityChoice';
import { useToast } from '../components/Toast/ToastContext';
import './PaymentPage.css';

type PaymentStatus = 'waiting' | 'completed' | 'failed' | 'expired';

type PaymentTiming =
  | 'immediate'
  | 'delay_15'
  | 'delay_30'
  | 'delay_45'
  | 'delay_60'
  | 'on_open';

interface OrderData {
  orderId: string;
  status: PaymentStatus;
  usdtAmount: number;
  currency: string;
  cryptoAmount: number;
  approximateTotalUsdt: number;
  address: string;
  expiresAt: string | null;
  serviceFeeUsdt?: number;
  transferFeeUsdt?: number;
  withDirectPayout?: boolean;
  description?: string | null;
  timeRemaining: {
    hours: number;
    minutes: number;
    total: number;
  };
  depositMetadata?: DepositMetadata;
}

interface PendingActivationData {
  orderId: string;
  status: 'pending_activation';
  paymentOption: 'instant' | 'batch';
  paymentTiming: PaymentTiming;
  activateAt: string | null;
  secondsUntilActive: number | null;
  message: string;
  description?: string | null;
}

interface DepositMetadata {
  provider?: string;
  trackId?: string;
  payAmount?: number;
  payCurrency?: string;
  network?: string;
  qrCodeUrl?: string;
}

type OrderResponse = OrderData | PendingActivationData;

type MessageTone = 'default' | 'success' | 'warning' | 'danger';
type ThemeMode = 'light' | 'dark';

const isPendingActivation = (
  data: OrderResponse | null,
): data is PendingActivationData =>
  Boolean(data && data.status === 'pending_activation');

const isRegularOrder = (data: OrderResponse | null): data is OrderData =>
  Boolean(data && data.status !== 'pending_activation');

const statusLabels: Record<PaymentStatus, string> = {
  waiting: 'Waiting',
  completed: 'Completed',
  failed: 'Failed',
  expired: 'Expired',
};

const statusCallouts: Partial<
  Record<
    PaymentStatus,
    {
      title: string;
      subtitle: string;
      tone: 'success' | 'danger';
    }
  >
> = {
  completed: {
    title: 'Payment confirmed',
    subtitle: 'We received your transfer and notified the merchant.',
    tone: 'success',
  },
  expired: {
    title: 'Payment window expired',
    subtitle:
      'Please create a fresh link to pay. Contact support if funds have already been sent.',
    tone: 'danger',
  },
  failed: {
    title: 'Payment failed',
    subtitle: 'Double-check the network and try again with a new address.',
    tone: 'danger',
  },
};

const statusOutcomeCopy: Record<
  Exclude<PaymentStatus, 'waiting'>,
  {
    title: string;
    subtitle: string;
    actionLabel: string;
  }
> = {
  completed: {
    title: 'Payment completed',
    subtitle: 'The merchant has been notified. You can close this window.',
    actionLabel: 'Back to home',
  },
  expired: {
    title: 'Payment link expired',
    subtitle: 'You can order another one.',
    actionLabel: 'Create a new payment',
  },
  failed: {
    title: 'Payment failed',
    subtitle: 'Payment link expired',
    actionLabel: 'Back to home',
  },
};

const paymentTimingLabels: Record<PaymentTiming, string> = {
  immediate: 'Immediately',
  delay_15: 'Delay 15 minutes',
  delay_30: 'Delay 30 minutes',
  delay_45: 'Delay 45 minutes',
  delay_60: 'Delay 60 minutes',
  on_open: 'On demand',
};

const paymentOptionLabels = {
  instant: 'Instant payout',
  batch: 'Batch payout',
};

const NETWORK_LABEL = 'TRON (TRC20)';

const getPreferredTheme = (): ThemeMode => {
  if (typeof window === 'undefined') {
    return 'light';
  }

  try {
    const stored = window.localStorage.getItem('payment-theme');
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }

    return window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  } catch {
    return 'light';
  }
};

const useMediaQuery = (query: string) => {
  const getMatches = () => {
    if (typeof window === 'undefined') {
      return false;
    }
    return window.matchMedia(query).matches;
  };

  const [matches, setMatches] = useState(getMatches);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const mediaQuery = window.matchMedia(query);
    setMatches(mediaQuery.matches);

    const listener = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', listener);
      return () => mediaQuery.removeEventListener('change', listener);
    }

    if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(listener);
      return () => mediaQuery.removeListener(listener);
    }

    return undefined;
  }, [query]);

  return matches;
};

const formatCountdown = (ms: number) => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const pad = (value: number) => value.toString().padStart(2, '0');

  if (hours > 0) {
    return `${hours}:${pad(minutes)}:${pad(seconds)}`;
  }

  return `${pad(minutes)}:${pad(seconds)}`;
};

const formatDateTime = (isoString: string | null) => {
  if (!isoString) {
    return 'Soon';
  }

  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) {
    return 'Soon';
  }

  return date.toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
};

export const PaymentPage: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const toast = useToast();
  const [orderPayload, setOrderPayload] = useState<OrderResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [message, setMessage] = useState('Waiting for payment...');
  const [messageTone, setMessageTone] = useState<MessageTone>('default');
  const [copyFeedback, setCopyFeedback] = useState<'address' | 'link' | null>(
    null,
  );
  const [qrFailed, setQrFailed] = useState(false);
  const feedbackTimeoutRef = useRef<number | null>(null);
  const [theme, setTheme] = useState<ThemeMode>(() => getPreferredTheme());
  const [isDevMode, setIsDevMode] = useState(false);
  const [devSimulating, setDevSimulating] = useState(false);
  const isMobile = useMediaQuery('(max-width: 640px)');
  const [shareExpanded, setShareExpanded] = useState(() => !isMobile);

  const shareUrl = useMemo(() => {
    if (typeof window === 'undefined' || !orderId) return '';
    return `${window.location.origin}/pay/${orderId}`;
  }, [orderId]);

  const fetchOrderData = useCallback(
    async (options?: { silent?: boolean }) => {
      if (!orderId) return;
      if (!options?.silent) {
        setLoading(true);
        setError(null);
      }

      try {
        const response = await fetch(`/api/orders/${orderId}`);
        if (!response.ok) {
          throw new Error('Order not found');
        }
        const data: OrderResponse = await response.json();

        setOrderPayload(data);
        setQrFailed(false);

        if (isPendingActivation(data)) {
          setTimeLeft((data.secondsUntilActive || 0) * 1000);
          setMessage(data.message);
          setMessageTone('warning');
        } else {
          setTimeLeft(data.timeRemaining?.total || 0);
          setMessage('Waiting for payment...');
          setMessageTone('default');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load order');
      } finally {
        if (!options?.silent) {
          setLoading(false);
        }
      }
    },
    [orderId],
  );

  useEffect(() => {
    fetchOrderData();
  }, [fetchOrderData]);

  // Check if dev simulate endpoint is available
  useEffect(() => {
    fetch('/api/dev/info')
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => data?.enabled && setIsDevMode(true))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      window.localStorage.setItem('payment-theme', theme);
    } catch {
      // ignored
    }
  }, [theme]);

  useEffect(() => {
    setShareExpanded(!isMobile);
  }, [isMobile]);

  // Timer countdown
  const hasCountdown = timeLeft > 0;
  useEffect(() => {
    if (!hasCountdown) return;
    const interval = window.setInterval(() => {
      setTimeLeft((prev) => Math.max(0, prev - 1000));
    }, 1000);

    return () => window.clearInterval(interval);
  }, [hasCountdown]);

  // Auto refresh when pending activation countdown finishes
  useEffect(() => {
    if (!isPendingActivation(orderPayload)) {
      return;
    }

    if (timeLeft > 0) {
      return;
    }

    const timeout = window.setTimeout(() => {
      fetchOrderData({ silent: true });
    }, 2000);

    return () => window.clearTimeout(timeout);
  }, [orderPayload, timeLeft, fetchOrderData]);

  // Check order status for active orders
  const checkOrderStatus = useCallback(async () => {
    if (!orderId || !isRegularOrder(orderPayload)) return;

    try {
      const response = await fetch(`/pay/${orderId}/status`);
      const data = await response.json();

      if (data.status === 'completed' || data.status === 'paid') {
        setMessage('Payment received!');
        setMessageTone('success');
        await fetchOrderData({ silent: true });
      } else if (data.status === 'expired') {
        setMessage('Order expired');
        setMessageTone('danger');
        await fetchOrderData({ silent: true });
      } else if (data.status === 'pending_activation') {
        await fetchOrderData({ silent: true });
      } else {
        setMessage(data.message || 'Waiting for payment...');
        setMessageTone('warning');
      }
    } catch (err) {
      console.error('Failed to check status:', err);
      setMessage('Unable to check order status');
      setMessageTone('danger');
    }
  }, [orderId, orderPayload, fetchOrderData]);

  const shouldPollStatus =
    isRegularOrder(orderPayload) && orderPayload.status === 'waiting';

  useEffect(() => {
    if (!shouldPollStatus) return;

    const interval = window.setInterval(checkOrderStatus, 5000);
    const timeout = window.setTimeout(checkOrderStatus, 1500);

    return () => {
      window.clearInterval(interval);
      window.clearTimeout(timeout);
    };
  }, [shouldPollStatus, checkOrderStatus]);

  useEffect(
    () => () => {
      if (feedbackTimeoutRef.current) {
        window.clearTimeout(feedbackTimeoutRef.current);
      }
    },
    [],
  );

  const toggleTheme = () => {
    setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));
  };

  const triggerFeedback = (type: 'address' | 'link') => {
    setCopyFeedback(type);
    if (feedbackTimeoutRef.current) {
      window.clearTimeout(feedbackTimeoutRef.current);
    }
    feedbackTimeoutRef.current = window.setTimeout(
      () => setCopyFeedback(null),
      1500,
    );
  };

  const copyAddress = async () => {
    if (!isRegularOrder(orderPayload)) return;
    try {
      await navigator.clipboard.writeText(orderPayload.address);
      triggerFeedback('address');
    } catch (err) {
      console.error('Failed to copy address', err);
    }
  };

  const copyShareUrl = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      triggerFeedback('link');
    } catch (err) {
      console.error('Failed to copy link', err);
    }
  };

  const sharePayment = async () => {
    if (!isRegularOrder(orderPayload) || !shareUrl) return;
    const text = `Pay ${orderPayload.cryptoAmount} ${orderPayload.currency} - Order #${orderPayload.orderId}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Payment request',
          text,
          url: shareUrl,
        });
      } catch {
        // ignored (user cancelled share)
      }
    } else {
      copyShareUrl();
    }
  };

  const handleDevSimulate = async () => {
    if (!orderId || devSimulating) return;
    setDevSimulating(true);
    try {
      const response = await fetch(`/api/dev/simulate/payment/${orderId}`, {
        method: 'POST',
      });
      if (response.ok) {
        toast.success('Payment simulated successfully!', 'DEV');
        await fetchOrderData({ silent: true });
      } else {
        const errText = await response.text();
        console.error('Dev simulate failed:', errText);
        toast.error(`Simulate failed: ${errText}`, 'DEV Error');
      }
    } catch (err) {
      console.error('Dev simulate error:', err);
      toast.error('Network error during simulation', 'DEV Error');
    } finally {
      setDevSimulating(false);
    }
  };

  const addressCopied = copyFeedback === 'address';
  const linkCopied = copyFeedback === 'link';

  const countdownLabel =
    timeLeft > 0
      ? formatCountdown(timeLeft)
      : isPendingActivation(orderPayload)
      ? 'Starting soon'
      : 'Expired';
  const pageClassName = `payment-page ${theme === 'dark' ? 'dark' : ''}`;

  if (loading) {
    return (
      <div className={pageClassName}>
        <div className="payment-shell">
          <div className="payment-card placeholder-card">
            Loading order information...
          </div>
        </div>
      </div>
    );
  }

  if (error || !orderPayload) {
    return (
      <div className={pageClassName}>
        <div className="payment-shell">
          <div className="payment-card error-card">
            Error: {error || 'Order not found'}
          </div>
        </div>
      </div>
    );
  }

  if (isPendingActivation(orderPayload)) {
    return (
      <div className={pageClassName}>
        <div className="payment-shell">
          <div className="pending-card">
            <div className="pending-icon" aria-hidden>
              ⌛
            </div>
            <h1>Payment link scheduled</h1>
            <p className="pending-subtitle">
              This payment link will activate in {countdownLabel}.
            </p>
            <div className="pending-details">
              <div>
                <span>Payment option:</span>{' '}
                {paymentOptionLabels[orderPayload.paymentOption]}
              </div>
              <div>
                <span>Activation timing:</span>{' '}
                {paymentTimingLabels[orderPayload.paymentTiming]}
              </div>
              <div>
                <span>Scheduled activation:</span>{' '}
                {formatDateTime(orderPayload.activateAt)}
              </div>
              {orderPayload.description && (
                <div className="pending-description">
                  <span>Description:</span> {orderPayload.description}
                </div>
              )}
            </div>
            <p className="pending-note">
              We refresh the page automatically once the countdown ends, but you
              can also refresh manually.
            </p>
            <button
              className="btn-primary"
              type="button"
              onClick={() => fetchOrderData()}
            >
              Refresh now
            </button>
          </div>
        </div>
      </div>
    );
  }

  const status = orderPayload.status;
  const approximateTotal = Number(
    orderPayload.approximateTotalUsdt?.toFixed(2) ?? orderPayload.usdtAmount,
  );
  const baseAmount = Number(orderPayload.usdtAmount?.toFixed(2) || 0);
  const feesDifference = Math.max(
    0,
    Number((approximateTotal - baseAmount).toFixed(2)),
  );
  const networkFee = Number(
    Math.min(feesDifference, 0.1).toFixed(2),
  );
  const withDirectPayout = Boolean(orderPayload.withDirectPayout);
  const actualServiceFee = Number(orderPayload.serviceFeeUsdt ?? 0.5);
  const actualTransferFee = Number(orderPayload.transferFeeUsdt ?? 0.8);
  const payoutFeeApprox = withDirectPayout
    ? Math.max(
        0,
        Number(
          (
            feesDifference -
            actualServiceFee -
            actualTransferFee -
            networkFee
          ).toFixed(2),
        ),
      )
    : 0;
  const combinedServiceFee = actualServiceFee + actualTransferFee + payoutFeeApprox;
  const formatFee = (value: number) => `${value.toFixed(2)} USDT`;
  const networkFeeDisplay =
    networkFee > 0 ? `≈${networkFee.toFixed(2)} USDT` : 'Dynamic';
  const feeCoverageText = withDirectPayout
    ? '9.5 USDT covers address activation, sweep, and the second TRC20 transfer (deposit -> master -> your wallet) plus the TRX buffer.'
    : '5 USDT covers activating a fresh address and sweeping it back to the master wallet (~28k energy).';
  const commissionBreakdown = withDirectPayout
    ? `Current fee: ${formatFee(combinedServiceFee)} (${formatFee(actualServiceFee)} service + ${formatFee(
        actualTransferFee,
      )} transfer + ${formatFee(payoutFeeApprox)} payout)`
    : `Current fee: ${formatFee(combinedServiceFee)} (${formatFee(actualServiceFee)} service + ${formatFee(
        actualTransferFee,
      )} transfer)`;
  const commissionText = `${feeCoverageText} ${commissionBreakdown}`;
  const depositMetadata = orderPayload.depositMetadata;
  const providerName = depositMetadata?.provider ?? 'Plz Pay Me';
  const providerNetwork = depositMetadata?.network ?? NETWORK_LABEL;
  const providerInvoiceCurrency =
    depositMetadata?.payCurrency ?? orderPayload.currency;
  const providerInvoiceAmount =
    depositMetadata?.payAmount != null
      ? `${depositMetadata.payAmount.toFixed(2)} ${providerInvoiceCurrency}`
      : null;
  const providerTrackId = depositMetadata?.trackId;
  const providerInvoiceUrl = depositMetadata?.qrCodeUrl;

  const isExpired = status === 'expired' || timeLeft <= 0;
  const isActionable = status === 'waiting' && !isExpired;
  const showQr = !qrFailed && isActionable;
  const statusTickerText = (() => {
    const uppercaseLabel = statusLabels[status].toUpperCase();
    if (status === 'waiting') {
      if (!isExpired && timeLeft > 0) {
        return `${uppercaseLabel} · expires in ${countdownLabel}`;
      }
      return `${uppercaseLabel} · expired`;
    }
    return uppercaseLabel;
  })();
  const calloutCopy = statusCallouts[status];
  const sharePanelId = `share-panel-${orderPayload.orderId}`;
  const shareRowClass = `share-row ${shareExpanded ? 'expanded' : 'collapsed'}`;

  if (status !== 'waiting') {
    const outcome = statusOutcomeCopy[status as Exclude<PaymentStatus, 'waiting'>];
    return (
      <div className={pageClassName}>
        <div className="payment-shell">
          <div className={`result-card ${status}`}>
            <div className="result-icon" aria-hidden>
              {status === 'completed' && (
                <svg width="40" height="40" viewBox="0 0 24 24">
                  <path
                    d="M6 12l4 4 8-8"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
              {status === 'failed' && (
                <svg width="40" height="40" viewBox="0 0 24 24">
                  <path
                    d="M15 9l-6 6m0-6l6 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <circle
                    cx="12"
                    cy="12"
                    r="9"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                </svg>
              )}
              {status === 'expired' && (
                <svg width="40" height="40" viewBox="0 0 24 24">
                  <circle
                    cx="12"
                    cy="12"
                    r="9"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <path
                    d="M12 7v5l3 2"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>
            <h1>{outcome.title}</h1>
            <p className="result-subtitle">{outcome.subtitle}</p>
            <div className="result-amount">
              <span>Amount</span>
              <strong>
                {orderPayload.cryptoAmount} {orderPayload.currency}
              </strong>
            </div>
            <button
              className="btn-primary"
              type="button"
              onClick={() => window.location.assign('/')}
            >
              {outcome.actionLabel}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={pageClassName}>
      <div className="payment-shell">
        <div className="payment-card modern-layout">
          <header className="card-header">
            <div className="order-headline">
              <div className="order-meta">
                <span className="order-id">Order #{orderPayload.orderId}</span>
                <span className={`timer-chip subtle ${isExpired ? 'expired' : ''}`}>
                  {statusTickerText}
                </span>
              </div>
              <button
                className="theme-toggle"
                type="button"
                onClick={toggleTheme}
                aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
              >
                {theme === 'dark' ? (
                  <svg
                    className="theme-icon"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    aria-hidden
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="5"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      fill="none"
                    />
                    <line
                      x1="12"
                      y1="2"
                      x2="12"
                      y2="5"
                      stroke="currentColor"
                      strokeWidth="1.6"
                    />
                    <line
                      x1="12"
                      y1="19"
                      x2="12"
                      y2="22"
                      stroke="currentColor"
                      strokeWidth="1.6"
                    />
                    <line
                      x1="4.22"
                      y1="4.22"
                      x2="6.34"
                      y2="6.34"
                      stroke="currentColor"
                      strokeWidth="1.6"
                    />
                    <line
                      x1="17.66"
                      y1="17.66"
                      x2="19.78"
                      y2="19.78"
                      stroke="currentColor"
                      strokeWidth="1.6"
                    />
                    <line
                      x1="2"
                      y1="12"
                      x2="5"
                      y2="12"
                      stroke="currentColor"
                      strokeWidth="1.6"
                    />
                    <line
                      x1="19"
                      y1="12"
                      x2="22"
                      y2="12"
                      stroke="currentColor"
                      strokeWidth="1.6"
                    />
                    <line
                      x1="4.22"
                      y1="19.78"
                      x2="6.34"
                      y2="17.66"
                      stroke="currentColor"
                      strokeWidth="1.6"
                    />
                    <line
                      x1="17.66"
                      y1="6.34"
                      x2="19.78"
                      y2="4.22"
                      stroke="currentColor"
                      strokeWidth="1.6"
                    />
                  </svg>
                ) : (
                  <svg
                    className="theme-icon"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    aria-hidden
                  >
                    <path
                      d="M21 12.79A9 9 0 0 1 11.21 3 7 7 0 1 0 21 12.79Z"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
                <span>{theme === 'dark' ? 'Light mode' : 'Dark mode'}</span>
              </button>
            </div>
          </header>

          <section className="hero-amount">
            <div className="hero-value">
              {orderPayload.cryptoAmount} {orderPayload.currency}
            </div>
            <div className="hero-subtitle">Network: {NETWORK_LABEL}</div>
          </section>

          {orderPayload.description && (
            <section className="description-card">
              <p className="description-title">Description</p>
              <p className="description-text">{orderPayload.description}</p>
            </section>
          )}

          {isActionable && (
            <PayerIdentityChoice orderId={orderPayload.orderId} />
          )}

          {isDevMode && isActionable && (
            <section className="dev-simulate-section">
              <button
                className="btn-dev-simulate"
                type="button"
                onClick={handleDevSimulate}
                disabled={devSimulating}
              >
                {devSimulating ? '⏳ Simulating...' : '✅ Simulate payment (DEV)'}
              </button>
            </section>
          )}

          {calloutCopy && (
            <section className={`status-callout ${calloutCopy.tone}`}>
              <div className="callout-icon" aria-hidden>
                {calloutCopy.tone === 'success' ? (
                  <svg width="24" height="24" viewBox="0 0 24 24">
                    <path
                      d="M5 12.5L9.5 17L19 7.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <svg width="24" height="24" viewBox="0 0 24 24">
                    <path
                      d="M12 8v5"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                    />
                    <circle
                      cx="12"
                      cy="16.5"
                      r="1"
                      fill="currentColor"
                    />
                    <path
                      d="M12 3.5l8.5 16h-17Z"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </div>
              <div className="callout-copy">
                <h2>{calloutCopy.title}</h2>
                <p>{calloutCopy.subtitle}</p>
              </div>
            </section>
          )}

          {isActionable ? (
            <section className="info-row">
              <div className="qr-block">
                {showQr ? (
                  <img
                    src={`/pay/${orderPayload.orderId}/qr`}
                    alt="Payment QR code"
                    className="qr-code"
                    onError={() => setQrFailed(true)}
                  />
                ) : (
                  <div className="qr-placeholder">
                    QR code will be available once the payment link is active.
                  </div>
                )}
              </div>
              <div className="address-block">
                <p className="muted">Address</p>
                <code className="address-code">{orderPayload.address}</code>
                <button
                  className="btn-primary"
                  type="button"
                  onClick={copyAddress}
                  disabled={!orderPayload.address}
                >
                  {addressCopied ? 'Copied ✓' : 'Copy address'}
                </button>
              </div>
            </section>
          ) : (
            <section className="inactive-link-card">
              <div className="inactive-copy">
                <h3>Payment link is closed</h3>
                <p>
                  QR code and address are no longer available for this order.
                  Generate a fresh payment link to try again.
                </p>
              </div>
              <button
                className="btn-secondary ghost"
                type="button"
                onClick={() => fetchOrderData({ silent: true })}
              >
                Refresh status
              </button>
            </section>
          )}

          <section className="fees-card">
            <div className="fees-header">Fees &amp; breakdown</div>
            <ul className="fees-list">
              <li>
                <span className="fee-label">
                  Service fee (processing)
                  <span
                    className="fee-tooltip-trigger"
                    aria-label={commissionText}
                  >
                    i
                    <span className="fee-tooltip">{commissionText}</span>
                  </span>
                </span>
                <strong>{formatFee(combinedServiceFee)}</strong>
              </li>
              <li className="total">
                <span>You will pay:</span>
                <strong>{approximateTotal.toFixed(2)} USDT</strong>
              </li>
            </ul>
            <p className="product-note">
              Product total: {baseAmount.toFixed(2)} USDT
            </p>
          </section>

          {isActionable && (
            <section className={shareRowClass}>
              <button
                className="share-toggle"
                type="button"
                onClick={() => setShareExpanded((prev) => !prev)}
                aria-expanded={shareExpanded}
                aria-controls={sharePanelId}
              >
                <div>
                  <p className="share-label">Share payment link</p>
                  <p className="share-hint">
                    Send this link to another device or a teammate.
                  </p>
                </div>
                <svg
                  className={`chevron ${shareExpanded ? 'open' : ''}`}
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  aria-hidden
                >
                  <polyline
                    points="6 9 12 15 18 9"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <div
                id={sharePanelId}
                className="share-content"
                aria-hidden={!shareExpanded}
              >
                <code className="share-url">{shareUrl}</code>
                <div className="share-actions">
                  <button
                    className="btn-secondary"
                    type="button"
                    onClick={copyShareUrl}
                  >
                    {linkCopied ? 'Copied ✓' : 'Copy link'}
                  </button>
                  <button
                    className="icon-button"
                    type="button"
                    onClick={sharePayment}
                    aria-label="Share payment link"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="18" cy="5" r="3" />
                      <circle cx="6" cy="12" r="3" />
                      <circle cx="18" cy="19" r="3" />
                      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                    </svg>
                  </button>
                </div>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};
